<template>
  <div class="tree-selector">
    <tree-item
      v-for="(item, idx) in tree"
      :key="idx"
      :item="item"
      :value="value"
      :is-filtered="isFiltered"
      @select="onSelectItem"
    />
  </div>
</template>

<script>
import TreeItem from './TreeItem.vue';

export default {
  name: 'TreeSelector',

  components: {
    TreeItem,
  },

  props: {
    value: {
      type: String,
      required: true,
    },

    // Can probably be an Array,
    // but leaving object in case we want to show variable types
    options: {
      type: Object,
      required: true,
    },

    labelMappings: {
      type: Object,
      default: () => ({}),
    },

    isFiltered: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['select'],

  provide() {
    return {
      labels: this.labelMappings,
    };
  },

  data() {
    return {
      tree: [],
    };
  },

  created() {
    this.tree = this.generateMap(Object.keys(this.options));
  },

  watch: {
    options: {
      deep: true,
      handler(options) {
        this.tree = this.generateMap(Object.keys(options));
      },
    },
  },

  methods: {
    onSelectItem(key) {
      this.$emit('select', key);
    },

    /**
     * cleanPath replaces indexes in keyPath with template string (e.g. seasons.0 becomes seasons.{{season}})
     * @param {string} path
     * @returns {string}
     */
    cleanPath(path) {
      return path
        .replace('seasons.0', 'seasons.{{seasonId}}')
        .replace('teams.0', 'teams.{{teamId}}')
        .replace('players.0', 'players.{{playerId}}');
    },

    generateMap(options = [], rootKeyPath = '') {
      if (!options?.length) {
        return [];
      }

      // Setup map with root parents
      const map = options.reduce((memo, kp) => {
        const [key] = kp.split('.');
        const parentKey = rootKeyPath ? rootKeyPath.split('.').pop() : null;

        let label = key;
        if (key === '0' && parentKey) {
          label = this.labels[parentKey] || key;
        }

        const existing = memo.find((v) => v.key === key);
        if (existing) {
          return memo;
        }

        const childOptions = options
          .filter((o) => o.indexOf(`${key}.`) === 0)
          .map((o) => o.substr(o.indexOf('.') + 1));

        const keyPath = [rootKeyPath, key].filter(Boolean).join('.');

        // Use "clean" path to check if selected, since we use handlebar-style vars
        const cleanPath = this.cleanPath(keyPath + (childOptions?.length ? '.' : ''));
        const selected = (
          cleanPath[cleanPath.length - 1] === '.'
            ? this.value.indexOf(cleanPath) === 0 // partial path check
            : this.value === cleanPath // full path comparison
        );

        memo.push({
          label,
          key,
          selected,
          children: this.generateMap(childOptions, keyPath),
        });

        return memo;
      }, []);

      return map;
    },
  },
};
</script>
